require('./RACompareExpectedProfitability.scss');

// @vendor
const PropTypes = require('prop-types');
const React = require('react');
const classnames = require('classnames');
const { min, max, map, last } = require('lodash');

// @components
const RAProjectionChart = require('../RAProjectionChart/RAProjectionChart');
const RAPillButtons = require('../RAPillButtons/RAPillButtons');
const RAProbabilityTag = require('../RAProbabilityTag/RAProbabilityTag');

// @utilities
const { FormattedText } = require('core/i18n').i18n;
const FormattedTextWithComponents = require('components/broker/brokerFormattedTextWithComponents/brokerFormattedTextWithComponents');

// @constants
const strategyText = {
    1: 'Kuala Lumpur',
    2: 'Shanghai',
    3: 'Taipei',
    4: 'New York',
    5: 'Dubai',
};

const strategyIcons = {
    1: 'icon-KualaLumpur-small',
    2: 'icon-shangai-small',
    3: 'icon-taipei-small', // @TODO: test it when ezequiel upload icon-fonts
    4: 'icon-NewYork-small',
    5: 'icon-dubai-small',
};

const CHART_ID = 'compareProfitability';
const LOWEST_KEY = 'one';
const HIGHEST_KEY = 'ninetyNine';

class RACompareExpectedProfitability extends React.Component {
    buildComparedSubtitle(isDisabled, comparedProfile) {
        let content;
        if (isDisabled) {
            content = (
                <FormattedText value="raCompareProfitability-newProfile" />
            );
        } else if (comparedProfile) {
            const comparedIcon = `ra-compare-expected-profitability__icon ${strategyIcons[comparedProfile]}`;
            content = (
                <div className="ra-compare-expected-profitability__compared-container">
                    <i className={comparedIcon} />
                    <FormattedTextWithComponents
                        value="raCompareProfitability-raCompareProfitability_comparedProfile"
                        injectedComponents={[strategyText[comparedProfile]]}
                    />
                </div>
            );
        }

        return content;
    }

    getYDomain(
        currentChartData,
        comparedChartData,
        goalAmount = 0,
        isDisabled,
    ) {
        if (
            currentChartData &&
            comparedChartData.length != 0 &&
            comparedChartData.length === currentChartData.length &&
            !isDisabled
        ) {
            const minMaxPairs = map(currentChartData, (v, idx) => {
                if (goalAmount !== 0) {
                    return [
                        min([
                            v[LOWEST_KEY],
                            comparedChartData[idx][LOWEST_KEY],
                            goalAmount,
                        ]),
                        max([
                            v[HIGHEST_KEY],
                            comparedChartData[idx][HIGHEST_KEY],
                            goalAmount,
                        ]),
                    ];
                } else {
                    return [
                        min([
                            v[LOWEST_KEY],
                            comparedChartData[idx][LOWEST_KEY],
                        ]),
                        max([
                            v[HIGHEST_KEY],
                            comparedChartData[idx][HIGHEST_KEY],
                        ]),
                    ];
                }
            });

            return last(minMaxPairs);
        } else {
            if (goalAmount !== 0) {
                return [
                    d => (d < goalAmount ? d : goalAmount),
                    d => (d > goalAmount ? d : goalAmount),
                ];
            } else {
                return ['dataMin', d => (d ? d : 1000)];
            }
        }
    }

    formatter(value) {
        return (
            <FormattedText
                value="rentabilityGraph-nYears"
                injectedStrings={[value]}
            />
        );
    }

    render() {
        const {
            actualAmount,
            currentChartData,
            comparedProfile,
            currentProfile,
            comparedChartData,
            chartYears,
            loading,
            changeYears,
            isDisabled,
            currentProbability,
            hasGoal,
            goalAmount,
            tickInfo,
        } = this.props;
        const disabledClassname = classnames(
            'ra-compare-expected-profitability__projection-charts',
            {
                'ra-compare-expected-profitability--disabled': isDisabled,
            },
        );
        const subText = isDisabled ? (
            <p className="ra-compare-expected-profitability__subtext">
                <FormattedText value="raCompareProfitability-selectDifferentProfile" />
            </p>
        ) : (
            <span />
        );
        const yDomain = this.getYDomain(
            currentChartData,
            comparedChartData,
            goalAmount,
            isDisabled,
        );
        const comparedSubtitle = this.buildComparedSubtitle(
            isDisabled,
            comparedProfile,
        );
        const currentIcon = `ra-compare-expected-profitability__icon ${strategyIcons[currentProfile]}`;
        const probabilitySection = currentProbability ? (
            <div className="ra-compare-expected-profitability__probability">
                <FormattedText value="raCompareProfitability-raCompareProfitability_probability" />
                <div className="ra-compare-expected-profitability__tag-wrapper">
                    <RAProbabilityTag label={currentProbability} />
                </div>
            </div>
        ) : null;
        const buttonsSection = !hasGoal ? (
            <div className="ra-compare-expected-profitability__buttons">
                <RAPillButtons
                    buttons={[2, 10, 25]}
                    changeOption={changeYears}
                    selectedOption={chartYears}
                    formatter={this.formatter}
                />
            </div>
        ) : null;

        const el = (
            <div className="ra-compare-expected-profitability">
                <div className="ra-compare-expected-profitability__header">
                    <h3 className="ra-compare-expected-profitability__title">
                        <FormattedText value="raCompareProfitability-title" />
                    </h3>
                    {buttonsSection}
                </div>
                <div className="row">
                    <div className="ra-compare-expected-profitability__projection-container">
                        <div className="ra-compare-expected-profitability__projection-charts">
                            <div className="ra-compare-expected-profitability__current-container">
                                <div className="ra-compare-expected-profitability__icon-container">
                                    <i className={currentIcon} />
                                </div>
                                <div className="ra-compare-expected-profitability__subtitle-container">
                                    <div className="ra-compare-expected-profitability__subtitle">
                                        <FormattedTextWithComponents
                                            value="raCompareProfitability-raCompareProfitability_currentProfile"
                                            injectedComponents={[
                                                strategyText[currentProfile],
                                            ]}
                                        />
                                    </div>
                                    {probabilitySection}
                                </div>
                            </div>
                            <RAProjectionChart
                                data={currentChartData}
                                years={chartYears}
                                syncId={CHART_ID}
                                margin={{ top: 30, right: 30, bottom: 30 }}
                                loading={loading}
                                yDomain={yDomain}
                                isObjectiveTracking={hasGoal}
                                objAmount={goalAmount}
                                actualAmount={actualAmount}
                                smallObjectiveDot={true}
                                tickInfo={tickInfo}
                            />
                        </div>
                        <div className={disabledClassname}>
                            <div className="ra-compare-expected-profitability__subtitle">
                                {comparedSubtitle}
                            </div>
                            {subText}
                            <RAProjectionChart
                                data={comparedChartData}
                                years={chartYears}
                                syncId={CHART_ID}
                                margin={{
                                    top: 30,
                                    right: 30,
                                    bottom: 20,
                                    left: 3,
                                }}
                                loading={loading}
                                disabled={isDisabled}
                                small={isDisabled}
                                yDomain={yDomain}
                                isObjectiveTracking={hasGoal}
                                objAmount={goalAmount}
                                actualAmount={actualAmount}
                                smallObjectiveDot={true}
                                tickInfo={tickInfo}
                            />
                        </div>
                    </div>
                </div>
                <div className="ra-compare-expected-profitability__footer">
                    <div className="row">
                        <div className="col-md-8 col-md-offset-2">
                            <div className="ra-expected-profitability__legends">
                                <div className="row no-gutter">
                                    <div className="col-md-4">
                                        <div className="ra-expected-profitability__legend ra-expected-profitability--grey">
                                            <p className="ra-expected-profitability__legend-text">
                                                <div>
                                                    <span className="ra-expected-profitability__circle" />
                                                    <FormattedText value="rentabilityGraph-legendTitleFirst" />
                                                </div>
                                                <p className="ra-expected-profitability__legend-info">
                                                    <FormattedText value="rentabilityGraph-legendInfoFirst" />
                                                </p>
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="ra-expected-profitability__legend ra-expected-profitability--white">
                                            <p className="ra-expected-profitability__legend-text">
                                                <div>
                                                    <span className="ra-expected-profitability__circle" />
                                                    <FormattedText value="rentabilityGraph-legendTitleMiddle" />
                                                </div>
                                                <p className="ra-expected-profitability__legend-info">
                                                    <FormattedText value="rentabilityGraph-legendInfoMiddle" />
                                                </p>
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="ra-expected-profitability__legend ra-expected-profitability--light-blue">
                                            <p className="ra-expected-profitability__legend-text">
                                                <div>
                                                    <span className="ra-expected-profitability__circle" />
                                                    <FormattedText value="rentabilityGraph-legendTitleLast" />
                                                </div>
                                                <p className="ra-expected-profitability__legend-info">
                                                    <FormattedText value="rentabilityGraph-legendInfoLast" />
                                                </p>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-12">
                            <p className="ra-compare-expected-profitability__footer-text">
                                <FormattedText value="rentabilityGraph-text" />
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        );
        return el;
    }
}

RACompareExpectedProfitability.propTypes = {
    actualAmount: PropTypes.number,
    changeYears: PropTypes.func.isRequired,
    chartButtons: PropTypes.array.isRequired,
    chartYears: PropTypes.number.isRequired,
    comparedChartData: PropTypes.array.isRequired,
    comparedProfile: PropTypes.string,
    currentProfile: PropTypes.string,
    currentChartData: PropTypes.array.isRequired,
    currentProbability: PropTypes.string.isRequired,
    goalAmount: PropTypes.number,
    hasGoal: PropTypes.bool,
    isDisabled: PropTypes.bool,
    loading: PropTypes.bool,
    tickInfo: PropTypes.number,
};

RACompareExpectedProfitability.defaultProps = {
    hasGoal: false,
    goalAmount: 0,
    actualAmount: 0,
};
module.exports = RACompareExpectedProfitability;
